import { request } from '../utils/api';
import { requireSessionTenantId } from '../utils/tenantContext';

/**
 * Get tenant ID from the logged-in session. Used for admin attendance APIs.
 * @returns {number}
 */
export function getTenantId() {
  return requireSessionTenantId();
}

/**
 * Add attendance record by admin (e.g. when user forgot to clock in).
 * POST /attendance/admin/add
 * Times are sent as local datetime strings (e.g. "YYYY-MM-DDTHH:mm:00") so the API
 * stores the exact date/time the user selected, without UTC conversion.
 * @param {Object} data
 * @param {number} data.userId
 * @param {number} [data.tenantId] - optional, defaults to current user's tenantId
 * @param {string} data.clockInTime - local datetime string (user-selected)
 * @param {string} data.clockOutTime - local datetime string (user-selected)
 * @param {string} [data.breakInTime] - local datetime string, optional (legacy)
 * @param {string} [data.breakOutTime] - local datetime string, optional (legacy)
 * @param {Array<{ id?: number, breakInTime: string, breakOutTime: string }>} [data.breaks]
 * @param {string} [data.notes]
 * @returns {Promise<{ success: boolean, message?: string, attendance?: object }>}
 */
export async function addAttendanceByAdmin(data) {
  const tenantId = data.tenantId ?? getTenantId();
  const payload = {
    userId: data.userId,
    tenantId,
    clockInTime: data.clockInTime,
    clockOutTime: data.clockOutTime,
    ...(Array.isArray(data.breaks) ? { breaks: data.breaks } : {}),
    ...(data.breakInTime && { breakInTime: data.breakInTime }),
    ...(data.breakOutTime && { breakOutTime: data.breakOutTime }),
    ...(data.notes != null && data.notes !== '' && { notes: data.notes }),
  };
  return request('/attendance/admin/add', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * Update an existing attendance record.
 * PUT /attendance/:attendanceId
 * @param {number} attendanceId
 * @param {Object} data - clockInTime, clockOutTime, breaks?, breakInTime?, breakOutTime?, notes
 * @returns {Promise<object>}
 */
export async function updateAttendance(attendanceId, data) {
  return request(`/attendance/${attendanceId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Update session remark (one field per attendance session — not clock in/out).
 * Kept for future UI (`SHOW_SESSION_REMARK_UI`); not called from current screens.
 * PATCH /attendance/:attendanceId/session-remark
 * @param {number} attendanceId
 * @param {{ remark: string | null }} data
 */
export async function updateSessionRemark(attendanceId, data) {
  return request(`/attendance/${attendanceId}/session-remark`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}
